import { type ReactNode } from 'react'

import { IconButton, Pagination, Space } from '@procurify/ui'
import styled from 'styled-components'

import { configureColumn as col, DataTable } from '@webapp/components/DataTable'
import { type DatatableColumnConfig } from '@webapp/components/DataTable/types/DataTable.types'
import { type CustomField } from '@webapp/core/entities/internal/CustomField'
import { useProcurifyIntl } from '@webapp/hooks'
import messages from './messages'

// Full-width override so the inline-edit cell fills the DataTable cell
// (ui-memory "Full-Width Components in DataTable Cells")
const FullWidthCellDecoratorStyled = styled.div`
  width: 100%;

  > div {
    display: block;
    width: 100% !important;
  }
`

export interface IPOCustomFieldConfigTableProps {
  fields: CustomField[]
  isLoading: boolean
  page: number
  numPages: number
  onPrevPage: () => void
  onNextPage: () => void
  onDeleteField?: (field: CustomField) => void
  renderNameCell?: (field: CustomField) => ReactNode
}

export const POCustomFieldConfigTable = ({
  fields,
  isLoading,
  page,
  numPages,
  onPrevPage,
  onNextPage,
  onDeleteField,
  renderNameCell,
}: IPOCustomFieldConfigTableProps) => {
  const intl = useProcurifyIntl()

  const columns: DatatableColumnConfig<any, CustomField>[] = [
    col('BASIC', {
      label: intl.formatMessage(messages.columnFieldDescription),
      getter: (field) => (renderNameCell ? renderNameCell(field) : field.name),
      decorate: (cell) => (
        <FullWidthCellDecoratorStyled>{cell}</FullWidthCellDecoratorStyled>
      ),
    }),
    col('BASIC', {
      label: '',
      getter: (field) => (
        <IconButton
          icon='delete'
          aria-label={intl.formatMessage(messages.deleteFieldButtonLabel, {
            name: field.name,
          })}
          htmlTitle={intl.formatMessage(messages.deleteFieldButtonTitle)}
          onClick={() => onDeleteField?.(field)}
        />
      ),
    }),
  ]

  const handlePageChange = (newPage: number) => {
    if (newPage > page) {
      onNextPage()
    } else if (newPage < page) {
      onPrevPage()
    }
  }

  return (
    <Space>
      <DataTable<CustomField>
        id='po-custom-field-config-table'
        aria-label={intl.formatMessage(messages.tableAriaLabel)}
        data={fields}
        isLoading={isLoading}
        columns={columns}
        customizableColumns={false}
        persistCustomizedColumns={false}
        emptyStateText={intl.formatMessage(messages.emptyStateText)}
        emptyStateSize='small'
      />
      <Space direction='horizontal' justify='end'>
        <Pagination
          currentPage={page}
          numPages={numPages}
          onPageChange={handlePageChange}
        />
      </Space>
    </Space>
  )
}

POCustomFieldConfigTable.displayName = 'POCustomFieldConfigTable'
